import { Pipe, PipeTransform, inject } from "@angular/core";
import { UtilService } from "../providers/util.service";

// Locale-aware replacement for Angular's built-in `date` pipe.
// Angular's DatePipe formats against LOCALE_ID, which the app never sets, so it
// always falls back to en-US (MM/DD/YYYY). This pipe formats via the user's
// resolved locale — UtilService.dateLocale, the single chokepoint that honours
// the region the user picked in their profile (falling back to the platform's
// region-aware default). No Angular locale data needs to be bundled.
//
// Supported `format` values mirror the Angular named formats currently in use:
//   'short'      -> short date + short time
//   'shortTime'  -> short time only
//   'mediumDate' -> medium date only
// For fixed machine formats (e.g. 'yyyy-MM-dd' form inputs) keep using the
// built-in DatePipe — those are intentionally locale-independent.
//
// Impure so it re-renders when the resolved locale changes (e.g. the profile
// loads after login, or the user changes their region in settings).

type LocaleDateFormat = "short" | "shortTime" | "mediumDate";

const FORMAT_OPTIONS: Record<LocaleDateFormat, Intl.DateTimeFormatOptions> = {
  short: { dateStyle: "short", timeStyle: "short" },
  shortTime: { timeStyle: "short" },
  mediumDate: { dateStyle: "medium" },
};

@Pipe({
  name: "localeDate",
  pure: false,
})
export class LocaleDatePipe implements PipeTransform {
  private readonly util = inject(UtilService);

  transform(
    value: number | string | Date | null | undefined,
    format: LocaleDateFormat = "short"
  ): string {
    if (value === null || value === undefined || value === "") return "";

    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return "";

    const options = FORMAT_OPTIONS[format] ?? FORMAT_OPTIONS.short;
    return new Intl.DateTimeFormat(this.util.dateLocale, options).format(date);
  }
}
